import { NgFor, NgIf, NgClass, DecimalPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, OnDestroy, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatOptionModule, MatOption, MatOptgroup } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { FormDataHelper, TranslateHelper } from '@app/_helpers';
import { VendorRequestService, AuthenticationService, VendorPaymentPlanService, VendorPlanFeatureService, CountriesService } from '@app/_services';
import { SubscriptionService } from '@app/_services/subscription.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ClickDirective } from '../../../../shared/directives/click.directive';
import { ItemCategoriesSelectComponent } from '@app/pages/quotation-requests/pages/page-rfq-request/components/item-categories-select/item-categories-select.component';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { CurrencyConvertPipe } from '@shared/pipes/currency-convert.pipe';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { environment } from '@environments/environment';

// Business Activities Dialog Component
@Component({
    selector: 'app-business-activities-dialog',
    templateUrl: './business-activities-dialog.component.html',
    styleUrls: ['./business-activities-dialog.component.scss'],
    standalone: true,
    imports: [MatDialogModule, MatButtonModule, MatIconModule, MatInputModule, ReactiveFormsModule, FormsModule, NgFor, NgIf, TranslateModule, MatProgressSpinnerModule]
})
export class BusinessActivitiesDialogComponent implements OnInit {
    activities: any[] = [];
    filteredActivities: any[] = [];
    selectedActivities: any[] = [];
    searchText: string = '';
    isLoading: boolean = false;

    get dir(): 'ltr' | 'rtl' {
        return this.translate?.currentLang === 'ar' ? 'rtl' : 'ltr';
    }
    
    constructor(
        public dialogRef: MatDialogRef<BusinessActivitiesDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        public translateHelper: TranslateHelper,
        private translate: TranslateService,
        private http: HttpClient
    ) {
        this.selectedActivities = data.selectedActivities || [];
    }
    
    ngOnInit(): void {
        this.loadActivities();
    }
    
    loadActivities(): void {
        this.isLoading = true;
        // Call public API to get active industry activities
        this.http.get<any>(`${environment.apiUrl}/industry-activities`, { params: { status: 'A' } })
            .subscribe({
                next: (result) => {
                    
                    if (result.code === 200) {
                        this.activities = result.data || [];
                        this.filteredActivities = [...this.activities];
                    } else {
                        console.error('Failed to load activities:', result);
                    }
                    this.isLoading = false;
                },
                error: (error) => {
                    console.error('Error loading activities:', error);
                    this.isLoading = false;
                }
            });
    }
    
    filterActivities(): void {
        if (!this.searchText || this.searchText.trim() === '') {
            this.filteredActivities = [...this.activities];
            return;
        }
        
        const search = this.searchText.toLowerCase().trim();
        this.filteredActivities = this.activities.filter(activity => {
            // Get localized name and description, with fallback to EN or empty string
            const nameLocalized = this.translateHelper.localize(activity.name);
            const descLocalized = this.translateHelper.localize(activity.description);
            
            const name = (nameLocalized || activity.name?.en || '').toLowerCase();
            const description = (descLocalized || activity.description?.en || '').toLowerCase();
            
            const matches = name.includes(search) || description.includes(search);
            return matches;
        });
        
        console.log('Search results:', {
            searchText: this.searchText,
            totalActivities: this.activities.length,
            filteredCount: this.filteredActivities.length
        });
    }
    
    isActivitySelected(activityId: string): boolean {
        return this.selectedActivities.some(a => a._id === activityId);
    }
    
    toggleActivity(activity: any): void {
        const index = this.selectedActivities.findIndex(a => a._id === activity._id);
        if (index > -1) {
            this.selectedActivities.splice(index, 1);
        } else {
            this.selectedActivities.push(activity);
        }
    }
    
    saveSelection(): void {
        this.dialogRef.close(this.selectedActivities);
    }
    
    onIconError(event: any): void {
        // Hide broken images
        if (event && event.target) {
            event.target.style.display = 'none';
        }
    }
    
    close(): void {
        this.dialogRef.close(null);
    }
}

// Billing Address Dialog Component
@Component({
    selector: 'app-billing-address-dialog',
    templateUrl: './billing-address-dialog.component.html',
    styleUrls: ['./billing-address-dialog.component.scss'],
    standalone: true,
    imports: [MatDialogModule, MatButtonModule, MatIconModule, MatSelectModule, MatOptionModule, MatOption, MatOptgroup, ReactiveFormsModule, FormsModule, NgFor, NgIf, TranslateModule]
})
export class BillingAddressDialogComponent {
    addressForm: UntypedFormGroup;
    selectedCountry: any = null;
    
    constructor(
        public dialogRef: MatDialogRef<BillingAddressDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private fb: UntypedFormBuilder
    ) {
        this.addressForm = this.fb.group({
            companyName: [{value: data.companyName || '', disabled: true}, [Validators.required]],
            country: [{value: data.country || null, disabled: true}, [Validators.required]],
            countryId: [data.countryId || null],
            cityId: [data.cityId || null, [Validators.required]],
            areaId: [data.areaId || null],
            streetAddress: [data.streetAddress || '', [Validators.required]],
            building: [data.building || '', [Validators.required]],
            block: [data.block || '', [Validators.required]],
            flat: [data.flat || '', [Validators.required]],
            road: [data.road || '', [Validators.required]],
            postalCode: [data.postalCode || '', [Validators.required]]
        });
        
        // Set selected country based on the country from data
        this.setSelectedCountryFromData();
    }
    
    setSelectedCountryFromData(): void {
        if (this.data.countryId && this.data.countriesWithCities) {
            this.selectedCountry = this.data.countriesWithCities.find(c => c._id === this.data.countryId);
        } else if (this.data.country && this.data.countriesWithCities) {
            // Try to match by country name
            this.selectedCountry = this.data.countriesWithCities.find(c => 
                (c.name?.en === this.data.country?.en || c.name?.en === this.data.country) ||
                (c.name?.ar === this.data.country?.ar || c.name?.ar === this.data.country)
            );
            if (this.selectedCountry) {
                this.addressForm.patchValue({ countryId: this.selectedCountry._id });
            }
        }
    }
    
    compareCountry = (c1: any, c2: any): boolean => {
        if (!c1 || !c2) return false;
        return c1.en === c2.en && c1.ar === c2.ar;
    };
    
    onCitySelect(areaId: any): void {
        this.addressForm.patchValue({ areaId: areaId });
    }
    
    saveAddress(): void {
        if (this.addressForm.valid) {
            // Get city name from cityId
            let cityName = '';
            const cityId = this.addressForm.get('cityId').value;
            if (cityId && this.selectedCountry && this.selectedCountry.areas) {
                for (const area of this.selectedCountry.areas) {
                    const city = area.cities?.find((c: any) => c._id === cityId);
                    if (city) {
                        cityName = this.data.translateHelper.localize(city.name);
                        break;
                    }
                }
            }
            
            // Include disabled fields and resolved city name in the result
            const formValue = {
                ...this.addressForm.value,
                companyName: this.addressForm.get('companyName').value,
                country: this.addressForm.get('country').value,
                city: cityName // Add the resolved city name
            };
            this.dialogRef.close(formValue);
        }
    }
}

// Confirmation Dialog Component
@Component({
    selector: 'app-confirm-step1-dialog',
    template: `
        <div class="confirmation-dialog">
            <div class="dialog-header">
                <div class="header-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
                        <path d="M12 16v-4"/>
                        <path d="M12 8h.01"/>
                    </svg>
                </div>
                <h2 class="dialog-title">{{ data.title }}</h2>
                <p class="dialog-subtitle">{{ data.message }}</p>
            </div>
            
            <div class="dialog-content">
                <div class="data-grid">
                    <div class="data-item">
                        <span class="data-label">{{ 'legal_business_name' | translate }}</span>
                        <span class="data-value">{{ data.formData.companyName }}</span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">{{ 'contact_name' | translate }}</span>
                        <span class="data-value">{{ data.formData.contactName }}</span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">{{ 'email' | translate }}</span>
                        <span class="data-value">{{ data.formData.email }}</span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">{{ 'mobile' | translate }}</span>
                        <span class="data-value">{{ data.formData.phone }}</span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">{{ 'cr_number' | translate }}</span>
                        <span class="data-value">{{ data.formData.crNumber }}</span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">{{ 'vat_no' | translate }}</span>
                        <span class="data-value">{{ data.formData.vatNo }}</span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">{{ 'country' | translate }}</span>
                        <span class="data-value">{{ data.formData.country }}</span>
                    </div>
                    <div class="data-item col-span-full" *ngIf="data.formData.billingAddress">
                        <span class="data-label">{{ 'company_billing_address' | translate }}</span>
                        <span class="data-value">{{ data.formData.billingAddress }}</span>
                    </div>
                </div>
            </div>
            
            <div class="dialog-actions">
                <button class="btn-secondary" [mat-dialog-close]="false">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    {{ 'edit_data' | translate }}
                </button>
                <button class="btn-primary" [mat-dialog-close]="true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {{ 'confirm_and_proceed' | translate }}
                </button>
            </div>
        </div>
    `,
    styles: [`
        .confirmation-dialog {
            padding: 24px;
            max-width: 550px;
        }
        .dialog-header {
            text-align: center;
            margin-bottom: 24px;
        }
        .header-icon {
            width: 64px;
            height: 64px;
            border-radius: 50%;
            background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 16px;
            color: white;
        }
        .dialog-title {
            font-size: 1.5rem;
            font-weight: 600;
            color: #1f2937;
            margin: 0 0 8px 0;
        }
        .dialog-subtitle {
            font-size: 0.875rem;
            color: #6b7280;
            margin: 0;
            line-height: 1.5;
        }
        .dialog-content {
            background: #f9fafb;
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 24px;
        }
        .data-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
        }
        .data-item {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }
        .data-item.col-span-full {
            grid-column: 1 / -1;
        }
        .data-label {
            font-size: 0.75rem;
            font-weight: 500;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.05em;
        }
        .data-value {
            font-size: 0.95rem;
            font-weight: 500;
            color: #1f2937;
            word-break: break-word;
        }
        .dialog-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
        }
        .btn-secondary, .btn-primary {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 10px 20px;
            border-radius: 8px;
            font-size: 0.875rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            border: none;
        }
        .btn-secondary {
            background: #f3f4f6;
            color: #374151;
        }
        .btn-secondary:hover {
            background: #e5e7eb;
        }
        .btn-primary {
            background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
            color: white;
        }
        .btn-primary:hover {
            background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }
        @media (max-width: 480px) {
            .data-grid {
                grid-template-columns: 1fr;
            }
            .dialog-actions {
                flex-direction: column;
            }
            .btn-secondary, .btn-primary {
                width: 100%;
                justify-content: center;
            }
        }
    `],
    standalone: true,
    imports: [MatDialogModule, MatButtonModule, TranslateModule, NgIf]
})
export class ConfirmStep1DialogComponent {
    constructor(
        public dialogRef: MatDialogRef<ConfirmStep1DialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {}
}

@UntilDestroy()
@Component({
    selector: 'app-vendor-form',
    templateUrl: './vendor-form.component.html',
    styleUrls: ['./vendor-form.component.scss'],
    providers: [FormDataHelper, TranslateHelper],
    standalone: true,
    imports: [FormsModule, ItemCategoriesSelectComponent, MatIconModule, ReactiveFormsModule, MatSelectModule, NgFor, NgClass, MatOptionModule, NgIf, MatButtonModule, MatInputModule, ClickDirective, TranslateModule, MatDialogModule, DecimalPipe, CurrencyConvertPipe]
})
export class VendorFormComponent implements OnInit, OnDestroy {

    constructor(public translateHelper: TranslateHelper,
        private fb: UntypedFormBuilder,
        private vendorRequestService: VendorRequestService,
        private vendorPaymentPlanService: VendorPaymentPlanService,
        private vendorPlanFeatureService: VendorPlanFeatureService,
        private subscriptionService: SubscriptionService,
        private fd: FormDataHelper,
        private router: Router,
        private route: ActivatedRoute,
        // private location: Location,
        private toastr: ToastrService,
        private translate: TranslateService,
        private authService: AuthenticationService,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private countriesService: CountriesService,
        private http: HttpClient) { }

    form = this.fb.group({
        companyName: [null, [Validators.required]],
        contactName: [null, [Validators.required]],
        email: [null, [Validators.required, Validators.email]],
        phone: [null, [Validators.required, Validators.pattern('^[0-9]*$')]],
        crNumber: [null, [Validators.required, Validators.pattern('^[0-9]*$')]],
        vatNo: [null, [Validators.required, Validators.pattern('^[0-9]*$')]],
        crCopy: [null, [Validators.required]],
        vatCopy: [null, [Validators.required]],
        logo: [null, [Validators.required]],
        country: [null, [Validators.required]],
        affiliate: [null],
        type: [null],
        billingAddress: [null, [Validators.required]],
        businessActivities: [[], [Validators.required]], // Always required for both vendor and supplier
        itemCategoryIds: [[]] // Auto-populated from selected activities' categories
    })
    @ViewChild('crCopy') crCopy: ElementRef;
    @ViewChild('vatCopy') vatCopy: ElementRef;
    @ViewChild('logo') logo: ElementRef;
    @ViewChildren('rejectedFileInput') rejectedFileInputs: QueryList<ElementRef>;

    // Multi-step form properties
    currentStep: number = 1;
    totalSteps: number = 5; // Updated to 5 steps with confirmation
    isLoading: boolean = false;
    vendorRequestId: string = null; // Store the ID after Step 1 save

    private step1Locked = false;

    // LocalStorage key for vendor request ID
    private readonly VENDOR_REQUEST_STORAGE_KEY = 'vendor_request_draft_id';

    // Subscription plans properties
    plans: any[] = [];
    filteredPlans: any[] = [];
    private featureMap = new Map<string, any>();
    private subFeatureMap = new Map<string, any>();
    private subFeaturesByFeatureId = new Map<string, any[]>();
    private subFeaturesLoaded: { [featureId: string]: boolean } = {};
    selectedBillingType: { [planId: string]: 'monthly' | 'yearly' } = {};
    expandedFeatures: { [planId: string]: boolean } = {};
    selectedPlan: any = null; // Track the selected plan
    private destroy$ = new Subject<void>();

    // Authorization tracking
    isAuthorized: boolean = false;
    vendorRequestStatus: string = null;
    authorizationCheckInterval: any = null;
    pendingBillingType: 'monthly' | 'yearly' = 'monthly'; // Store billing type for summary

    // Field rejection tracking
    fieldRejections: { fieldName: string; reason: string; rejectedAt: Date; resolved?: boolean }[] = [];
    isRejected: boolean = false;
    isUpdatingRejectedFields: boolean = false;
    rejectedFiles: { [key: string]: File } = {}; // Store files for rejected document fields

    // Existing document tracking (for loaded vendor requests)
    existingDocuments: { [key: string]: { filename: string; url: string } } = {};
    
    // Business Activities tracking
    selectedActivities: any[] = [];

    type: string;
    disableSubmitButton = false;
    // planId;
    // chosenPlan;
    // extraServices: string[] = [];

    countries = [
        {
            name: {
                en: "Bahrain",
                ar: "البحرين",
            }
        },
        {
            name: {
                en: "Saudi Arabia",
                ar: "السعودية",
            }
        },
        {
            name: {
                en: "United Arab Emirates",
                ar: "الإمارات العربية المتحدة",
            }
        }
    ];

    countriesWithCities: any[] = []; // Countries with areas and cities for dropdown

    fileErrors = {
        crCopy: {
            type: false,
            size: false
        },
        vatCopy: {
            type: false,
            size: false
        },
        logo: {
            type: false,
            size: false
        }
    };

    ngOnInit(): void {

        // Load countries with cities for billing address
        this.loadCountriesWithCities();

        this.type = this.route.snapshot.paramMap.get('type');
        this.form.get('type').setValue(this.type || "register");
        this.form.get('country').setValue(this.countries?.[0]?.name);

        // Auto-populate contact name and email from logged-in user
        this.populateUserData();

        // Two-way sync: companyName <-> billingAddress.companyName
        this.form.get('companyName').valueChanges.pipe(untilDestroyed(this))
            .subscribe(companyName => {
                const billingAddress = this.form.get('billingAddress').value;
                if (billingAddress && billingAddress.companyName !== companyName) {
                    this.form.get('billingAddress').patchValue(
                        { ...billingAddress, companyName },
                        { emitEvent: false }
                    );
                }
            });

        // Two-way sync: country <-> billingAddress.country
        this.form.get('country').valueChanges.pipe(untilDestroyed(this))
            .subscribe(country => {
                const billingAddress = this.form.get('billingAddress').value;
                if (billingAddress && !this.compareCountry(billingAddress.country, country)) {
                    this.form.get('billingAddress').patchValue(
                        { ...billingAddress, country },
                        { emitEvent: false }
                    );
                }
            });

        // Always try to load vendor request immediately if user is already logged in
        // This handles the case when navigating from apply-for-vendor page
        const currentUser = this.authService.currentUserValue;
        if (currentUser) {
            this.loadExistingVendorRequest();
        }

        // Also subscribe to auth state changes to handle delayed auth resolution
        this.authService.currentUser.pipe(
            untilDestroyed(this)
        ).subscribe(user => {
            // Load vendor request when user becomes available and we haven't loaded yet
            // Also check that vendorRequestId is not already set
            if (user && !this.vendorRequestId && !this.vendorRequestLoaded && !this.isLoading) {
                this.loadExistingVendorRequest();
            }
        });

        /*
        this.vendorRequestService.currentSelectedPlan$.subscribe((data) => {
            if (_.isEmpty(data)) {
                this.toastr.show("Please select a plan", "Error")
                this.router.navigate(['apply-for-vendor']);
                return;
            }
            this.planId = data.planId;
            this.extraServices = data.services;
            this.vendorPaymentPlanService.getPlan(this.planId).subscribe(
                (result) => {
                    if (result.code == 200) {
                        this.chosenPlan = result.data;
                        this.form.get('planId').patchValue(this.chosenPlan._id);
                    } else {
                        console.log(result.msg);
                        this.toastr.show("Error encountered while fetching plan data from the server", "Server Error")
                        this.location.back();
                    }
                }, (error) => {
                    console.log(error);
                    this.toastr.show("Error encountered before fetching plan data from the server", "Client Error")
                    this.location.back();
                }
            )
        }); */
    }

    // Flag to prevent multiple loads
    private vendorRequestLoaded = false;

    // Populate contact name and email from logged-in user
    private populateUserData(): void {
        const currentUser = this.authService.currentUserValue;
        if (currentUser) {
            // Set contact name from user's name field
            if (currentUser.name) {
                this.form.get('contactName').setValue(currentUser.name);
                this.form.get('contactName').disable(); // Make read-only
            }
            // Set email from user's email field
            if (currentUser.email) {
                this.form.get('email').setValue(currentUser.email);
                this.form.get('email').disable(); // Make read-only
            }
        } else {
            // Wait for auth to resolve and populate when user is available
            this.authService.currentUser.pipe(
                untilDestroyed(this)
            ).subscribe(user => {
                if (user) {
                    if (user.name && !this.form.get('contactName').value) {
                        this.form.get('contactName').setValue(user.name);
                        this.form.get('contactName').disable();
                    }
                    if (user.email && !this.form.get('email').value) {
                        this.form.get('email').setValue(user.email);
                        this.form.get('email').disable();
                    }
                }
            });
        }
    }

    // Load vendor request by ID from localStorage
    private loadVendorRequestById(requestId: string, currentUserId?: string): void {
        // Prevent multiple loads or if we already have this vendor request loaded
        if (this.vendorRequestLoaded || this.isLoading || (this.vendorRequestId && this.vendorRequestId === requestId)) {
            return;
        }

        this.vendorRequestLoaded = true;
        this.isLoading = true;
        this.vendorRequestService.getDraftById(requestId).pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                this.isLoading = false;
                if (response.code === 200 && response.data) {
                    // Verify the request belongs to the current user
                    if (currentUserId && response.data.userId && response.data.userId !== currentUserId) {
                        // Request belongs to a different user, clear localStorage
                        localStorage.removeItem(this.VENDOR_REQUEST_STORAGE_KEY);
                        this.vendorRequestLoaded = false;
                        // Try to load the current user's request instead
                        this.loadExistingVendorRequest();
                        return;
                    }
                    this.populateFormWithVendorRequest(response.data);
                } else {
                    // Clear invalid localStorage entry
                    localStorage.removeItem(this.VENDOR_REQUEST_STORAGE_KEY);
                }
            },
            error: (error) => {
                this.isLoading = false;
                // Clear invalid localStorage entry
                localStorage.removeItem(this.VENDOR_REQUEST_STORAGE_KEY);
                console.log('Error loading vendor request by ID:', error);
            }
        });
    }

    // Load existing vendor request for logged-in users
    private loadExistingVendorRequest(): void {
        // Prevent multiple loads or if we already have a vendor request ID
        if (this.vendorRequestLoaded || this.isLoading || this.vendorRequestId) {
            return;
        }

        // Check if user is logged in
        if (!this.authService.currentUserValue) {
            return;
        }

        // First, check if we have a vendor request ID in localStorage
        const storedRequestId = localStorage.getItem(this.VENDOR_REQUEST_STORAGE_KEY);
        if (storedRequestId) {
            this.loadVendorRequestById(storedRequestId, this.authService.currentUserValue._id);
            return;
        }

        this.vendorRequestLoaded = true;
        this.isLoading = true;
        this.vendorRequestService.getMyRequest().pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                this.isLoading = false;
                if (response.code === 200 && response.data) {
                    this.populateFormWithVendorRequest(response.data);
                }
            },
            error: (error) => {
                this.isLoading = false;
                // Don't show error for 404 (no existing request found)
                console.log('No existing vendor request found or error:', error);
            }
        });
    }

    // Populate form with vendor request data
    private populateFormWithVendorRequest(vendorRequest: any): void {
      
        this.vendorRequestId = vendorRequest._id;
        this.vendorRequestStatus = vendorRequest.status;
        
        // Save to localStorage for future page refreshes
        localStorage.setItem(this.VENDOR_REQUEST_STORAGE_KEY, vendorRequest._id);
        
        // Populate form with existing data
        this.form.patchValue({
            companyName: vendorRequest.companyName,
            contactName: vendorRequest.contactName,
            email: vendorRequest.email,
            phone: vendorRequest.phone,
            crNumber: vendorRequest.crNumber,
            vatNo: vendorRequest.vatNo,
            country: vendorRequest.country,
            affiliate: vendorRequest.affiliate,
            type: vendorRequest.type,
            billingAddress: vendorRequest.billingAddress
        });

        // Handle existing documents - store info and set placeholder values to satisfy validators
        const documentFields = ['crCopy', 'vatCopy', 'logo'];

        documentFields.forEach(field => {
            if (vendorRequest[field] && vendorRequest[field].filename) {
                
                // Store existing document info
                this.existingDocuments[field] = {
                    filename: vendorRequest[field].filename,
                    url: vendorRequest[field].url || vendorRequest[field].path
                };
                // Set a placeholder value to satisfy the required validator
                // We use an object with name property to match the File interface display pattern
                this.form.get(field)?.setValue({ name: vendorRequest[field].filename, existing: true });
                this.form.get(field)?.clearValidators();
                this.form.get(field)?.updateValueAndValidity();
                
            }
        });
       

        // Handle itemCategoryIds and businessActivities
        if (vendorRequest.itemCategoryIds) {
            this.form.get('itemCategoryIds').setValue(vendorRequest.itemCategoryIds);
        }
        
        // Handle businessActivities - need to fetch full activity data for display
        const businessActivitiesIds = this.normalizeIdArray(vendorRequest.businessActivities);
        if (businessActivitiesIds && Array.isArray(businessActivitiesIds) && businessActivitiesIds.length > 0) {
            this.form.get('businessActivities').setValue(businessActivitiesIds);
            this.loadActivitiesByIds(businessActivitiesIds);
        }

        // Store pending plan ID if exists - will be used after plans are loaded
        if (vendorRequest.planId) {
            this.pendingPlanId = vendorRequest.planId;
        }
        
        // Store billing type if exists
        if (vendorRequest.billingType) {
            this.pendingBillingType = vendorRequest.billingType;
        }

      
        
        this.isRejected = vendorRequest.status === 'rejected';
        if (vendorRequest.fieldRejections && vendorRequest.fieldRejections.length > 0) {
            this.fieldRejections = vendorRequest.fieldRejections.filter((r: any) => !r.resolved);
        } else {
            this.fieldRejections = [];
        }
        
      

        // Disable all step 1 fields since vendor request is already saved
        this.disableStep1Fields();
        
        // For rejected status, enable the rejected fields so they can be edited in Step 3
        if (this.isRejected && this.fieldRejections.length > 0) {
            this.enableRejectedFields();
        }

        // Restore the step based on status and step saved in database
        this.restoreStepFromVendorRequest(vendorRequest);
        
        // Trigger change detection to ensure form is rendered
        // Use setTimeout to ensure Angular has processed the form changes
        setTimeout(() => {
            this.cdr.detectChanges();
            this.toastr.info(this.translate.instant('draft_loaded_successfully') || 'Your previous data has been loaded');
        }, 0);
    }

    // Pending plan ID to be set after plans are loaded
    private pendingPlanId: string = null;

    // Restore the correct step based on vendor request status
    private restoreStepFromVendorRequest(vendorRequest: any): void {
        const status = vendorRequest.status;
        
        // Store billing type from vendor request if available
        if (vendorRequest.billingType) {
            this.pendingBillingType = vendorRequest.billingType;
        }

        // Payment completed - show confirmation step
        if (status === 'paid') {
            this.isAuthorized = true;
            if (vendorRequest.planId) {
                this.pendingPlanId = vendorRequest.planId;
                this.loadPlans();
            } else {
                this.loadPlans();
            }
            this.currentStep = 5;
            if (this.authorizationCheckInterval) {
                clearInterval(this.authorizationCheckInterval);
                this.authorizationCheckInterval = null;
            }
        }
        // Check if user is authorized (status = completed)
        else if (status === 'completed') {
            this.isAuthorized = true;
            // Load plans to get the selected plan details
            if (vendorRequest.planId) {
                this.pendingPlanId = vendorRequest.planId;
                this.loadPlans();
                // Go to step 4 (summary)
                this.currentStep = 4;
            } else {
                // Authorized but no plan, go to step 2 to select plan
                this.currentStep = 2;
                this.loadPlans();
            }
        } else if (status === 'plan') {
            // Plan selected, waiting for authorization - go to step 3
            this.currentStep = 3;
            this.isAuthorized = false;
            // Load plans to get the selected plan details for display
            if (vendorRequest.planId) {
                this.pendingPlanId = vendorRequest.planId;
                this.loadPlans();
            }
            // Start checking for authorization
            this.startAuthorizationCheck();
        } else if (status === 'new' || status === 'open') {
            // New request - go to step 2 to select plan
            this.currentStep = 2;
            this.loadPlans();
        } else if (status === 'rejected') {
            // Request has been rejected - stay on step 3 to show rejected fields
            this.currentStep = 3;
            this.isRejected = true;
            this.isAuthorized = false;
            // Load plans to display selected plan info
            if (vendorRequest.planId) {
                this.pendingPlanId = vendorRequest.planId;
                this.loadPlans();
            }
        } else {
            // Default to step 2 (plan selection) for any other status
            this.currentStep = 2;
            this.loadPlans();
        }
    }

    // Compare function for country mat-select
    compareCountry = (c1: any, c2: any): boolean => {
        if (!c1 || !c2) return false;
        return c1.en === c2.en && c1.ar === c2.ar;
    };

    // Disable all step 1 fields when vendor request is already saved
    private disableStep1Fields(): void {
        const step1Fields = ['companyName', 'contactName', 'email', 'phone', 'crNumber', 'vatNo', 'country', 'affiliate', 'type', 'billingAddress', 'businessActivities'];
        step1Fields.forEach(field => {
            const control = this.form.get(field);
            if (control) {
                control.disable();
            }
        });
        // Also disable itemCategoryIds if it exists
        const itemCategoriesControl = this.form.get('itemCategoryIds');
        if (itemCategoriesControl) {
            itemCategoriesControl.disable();
        }

        this.step1Locked = true;
    }

    // Enable only the rejected fields for editing
    private enableRejectedFields(): void {
        const rejectedFieldNames = this.fieldRejections
            .filter(r => !r.resolved)
            .map(r => r.fieldName);
        
        rejectedFieldNames.forEach(fieldName => {
            const control = this.form.get(fieldName);
            if (control) {
                control.enable();
                
                // For document fields that are rejected, do NOT populate rejectedFiles
                // The vendor must upload a new file
                // Only keep existingDocuments for reference, but don't add to rejectedFiles
            }
        });
    }

    // Check if a field is rejected (for visual indicator in template)
    isFieldRejected(fieldName: string): boolean {
        return this.fieldRejections.some(r => r.fieldName === fieldName && !r.resolved);
    }

    // Get rejection reason for a field
    getFieldRejectionReason(fieldName: string): string {
        const rejection = this.fieldRejections.find(r => r.fieldName === fieldName && !r.resolved);
        return rejection?.reason || '';
    }

    // Get all unresolved rejected field names
    getUnresolvedRejectedFields(): string[] {
        return this.fieldRejections
            .filter(r => !r.resolved)
            .map(r => r.fieldName);
    }

    // Get human-readable label for a field name
    getFieldLabel(fieldName: string): string {
        const labels: { [key: string]: string } = {
            'companyName': 'legal_business_name',
            'contactName': 'contact_name',
            'email': 'email',
            'phone': 'mobile',
            'crNumber': 'cr_number',
            'vatNo': 'vat_no',
            'country': 'country',
            'affiliate': 'affiliate',
            'type': 'business_type',
            'businessActivities': 'business_activities',
            'itemCategoryIds': 'categories',
            'crCopy': 'cr_copy',
            'vatCopy': 'vat_certificate',
            'logo': 'company_logo',
            'billingAddress': 'company_billing_address'
        };
        return labels[fieldName] || fieldName;
    }

    // Check if a field should use a select input
    isSelectField(fieldName: string): boolean {
        return ['country', 'type'].includes(fieldName);
    }

    // Check if a field is a document/file upload field
    isDocumentField(fieldName: string): boolean {
        return ['crCopy', 'vatCopy', 'logo'].includes(fieldName);
    }

    // Handle number-only input fields
    onNumberInput(event: any, fieldName: string): void {
        const input = event.target;
        const value = input.value;
        // Remove any non-numeric characters
        const numericValue = value.replace(/[^0-9]/g, '');
        if (value !== numericValue) {
            this.form.get(fieldName)?.setValue(numericValue);
            input.value = numericValue;
        }
    }

    // Trigger the file input for document uploads (for rejected fields in Step 3)
    triggerDocumentUpload(fieldName: string): void {
        // Find the file input with the matching data-field attribute
        const input = this.rejectedFileInputs?.find(el => 
            el.nativeElement.getAttribute('data-field') === fieldName
        );
        if (input) {
            input.nativeElement.click();
        }
    }

    // Handle file input change for rejected document fields
    handleRejectedFileInput(fieldName: string, files: FileList): void {
        if (files && files.length > 0) {
            const file = files[0];
            // Validate file type
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
            if (!allowedTypes.includes(file.type)) {
                this.toastr.error(this.translate.instant('invalid_file_type') || 'Invalid file type. Please upload PDF, JPG, or PNG.');
                return;
            }
            // Validate file size (max 5MB)
            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                this.toastr.error(this.translate.instant('file_too_large') || 'File is too large. Maximum size is 5MB.');
                return;
            }
            // Store the file
            this.rejectedFiles[fieldName] = file;
            // Also update the form control so it's included in submission
            this.form.get(fieldName)?.setValue(file);
        }
    }

    // Submit updated rejected fields
    submitRejectedFields(): void {
        if (this.isUpdatingRejectedFields) return;

        // Mark all rejected fields as touched to show validation errors
        this.fieldRejections.forEach(rejection => {
            const control = this.form.get(rejection.fieldName);
            if (control) {
                control.markAsTouched();
                control.markAsDirty();
                control.updateValueAndValidity();
            }
        });

        // Trigger change detection
        this.cdr.detectChanges();

        // Validate that all rejected fields are populated
        let hasEmptyFields = false;
        let hasInvalidFields = false;
        const emptyFieldNames: string[] = [];
        const invalidFieldLabels: string[] = [];
        
        this.fieldRejections.forEach(rejection => {
            const control = this.form.get(rejection.fieldName);
            
            // Check if it's a document field
            if (this.isDocumentField(rejection.fieldName)) {
                // For document fields, check if file is uploaded
                if (!this.rejectedFiles[rejection.fieldName]) {
                    hasEmptyFields = true;
                    emptyFieldNames.push(rejection.fieldName);
                    invalidFieldLabels.push(this.translate.instant(this.getFieldLabel(rejection.fieldName)));
                }
            } else {
                // For regular fields, check form control value
                if (control && (!control.value || control.value === '' || control.value === null)) {
                    hasEmptyFields = true;
                    emptyFieldNames.push(rejection.fieldName);
                    invalidFieldLabels.push(this.translate.instant(this.getFieldLabel(rejection.fieldName)));
                } else if (control && control.invalid) {
                    // Check for format validation errors
                    hasInvalidFields = true;
                    emptyFieldNames.push(rejection.fieldName);
                    invalidFieldLabels.push(this.translate.instant(this.getFieldLabel(rejection.fieldName)));
                }
            }
        });

        if (hasEmptyFields) {
            console.log('Empty fields:', emptyFieldNames);
            const fieldsList = invalidFieldLabels.join(', ');
            this.toastr.error(
                `${this.translate.instant('please_fill_all_required_fields')}: ${fieldsList}`,
                this.translate.instant('validation_error') || 'Validation Error',
                { timeOut: 6000 }
            );
            
            // Scroll to first error
            setTimeout(() => {
                const firstErrorElement = document.querySelector('.field-card .border-red-500, .field-card .text-red-600');
                if (firstErrorElement) {
                    firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
            return;
        }

        if (hasInvalidFields) {
            console.log('Invalid fields:', emptyFieldNames);
            const fieldsList = invalidFieldLabels.join(', ');
            this.toastr.error(
                `${this.translate.instant('please_check_field_formats')}: ${fieldsList}`,
                this.translate.instant('validation_error') || 'Validation Error',
                { timeOut: 6000 }
            );
            
            // Scroll to first error
            setTimeout(() => {
                const firstErrorElement = document.querySelector('.field-card .border-red-500, .field-card .text-red-600');
                if (firstErrorElement) {
                    firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
            return;
        }

        const unresolvedFields = this.getUnresolvedRejectedFields();
        if (unresolvedFields.length === 0) {
            this.toastr.info(this.translate.instant('no_rejected_fields_to_update') || 'No rejected fields to update');
            return;
        }

        // Gather updated values for rejected fields
        const updates: { [key: string]: any } = {};
        unresolvedFields.forEach(fieldName => {
            // For document fields, use the actual file from rejectedFiles
            if (this.isDocumentField(fieldName)) {
                if (this.rejectedFiles[fieldName]) {
                    updates[fieldName] = this.rejectedFiles[fieldName];
                }
            } else {
                // For regular fields, use form control value
                const control = this.form.get(fieldName);
                if (control) {
                    updates[fieldName] = control.value;
                }
            }
        });

        // If companyName is being updated, also update it in billingAddress
        if (updates['companyName']) {
            const currentBillingAddress = this.form.get('billingAddress').value || {};
            const updatedBillingAddress = {
                ...currentBillingAddress,
                companyName: updates['companyName']
            };
            updates['billingAddress'] = updatedBillingAddress;
            // Update the form as well for consistency
            this.form.get('billingAddress').patchValue(updatedBillingAddress, { emitEvent: false });
        }

        // If country is being updated, also update it in billingAddress
        if (updates['country']) {
            const currentBillingAddress = this.form.get('billingAddress').value || {};
            const updatedBillingAddress = {
                ...currentBillingAddress,
                country: updates['country']
            };
            updates['billingAddress'] = updatedBillingAddress;
            // Update the form as well for consistency
            this.form.get('billingAddress').patchValue(updatedBillingAddress, { emitEvent: false });
        }

        this.isUpdatingRejectedFields = true;
        this.vendorRequestService.updateRejectedFields(this.vendorRequestId, updates).subscribe({
            next: (response: any) => {
                this.isUpdatingRejectedFields = false;
                if (response.code === 200) {
                    this.toastr.success(this.translate.instant('rejected_fields_updated') || 'Fields updated successfully. Your request is now under review.');
                    
                    // If all rejections are resolved, status is back to 'plan' (waiting for authorization)
                    if (response.data?.allResolved) {
                        this.isRejected = false;
                        this.vendorRequestStatus = 'plan';
                        this.fieldRejections = [];
                        // Stay on step 3 - waiting for authorization again
                        // The admin will now review and authorize or reject again
                        this.startAuthorizationCheck();
                    }
                } else {
                    this.toastr.error(response.msg || this.translate.instant('failed_to_update_fields') || 'Failed to update fields');
                }
            },
            error: (error) => {
                this.isUpdatingRejectedFields = false;
                this.toastr.error(this.translate.instant('error_updating_fields') || 'Error updating fields');
                console.error('Error updating rejected fields:', error);
            }
        });
    }

    // ngOnDestroy lifecycle hook
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        // Clear authorization check interval
        if (this.authorizationCheckInterval) {
            clearInterval(this.authorizationCheckInterval);
            this.authorizationCheckInterval = null;
        }
    }

    // ============================================
    // COUNTRIES AND CITIES METHODS
    // ============================================

    // Load countries with areas and cities for billing address
    loadCountriesWithCities(): void {
        this.countriesService.getCountries().subscribe({
            next: (countries) => {
                if (countries && Array.isArray(countries)) {
                    this.countriesWithCities = countries;
                } else {
                    this.countriesWithCities = [];
                }
            },
            error: (error) => {
                console.error('Error loading countries with cities:', error);
                this.countriesWithCities = [];
            }
        });
    }

    // ============================================
    // SUBSCRIPTION PLANS METHODS
    // ============================================

    // Load subscription plans
    loadPlans(): void {
        // First, load active features and build a map for id -> feature
        this.vendorPlanFeatureService.getFeatures({ page: 1, limit: 1000, status: 'A' }).subscribe({
            next: (featuresRes) => {
                if (featuresRes?.code === 200) {
                    const features = featuresRes.data || [];
                    this.featureMap = new Map(features.map((f: any) => [f._id, f]));
                } else {
                    this.featureMap = new Map();
                }

                // Then load plans and enrich with populated features if missing
                this.vendorPaymentPlanService.getAllPlans().subscribe((result: any) => {
                    if (result.code == 200) {
                        this.plans = (result.data || []).map((plan: any) => {
                            if (Array.isArray(plan.subscription_features_populated) && plan.subscription_features_populated.length > 0) {
                                return plan; // already populated by backend
                            }
                            const ids: string[] = Array.isArray(plan.subscription_features) ? plan.subscription_features : [];
                            const populated = ids.map((id: string) => this.featureMap.get(id)).filter(Boolean);
                            return { ...plan, subscription_features_populated: populated };
                        });
                        this.filterPlans();
                    }
                });
            },
            error: () => {
                // Fallback: still load plans even if features call fails
                this.vendorPaymentPlanService.getAllPlans().subscribe((result: any) => {
                    if (result.code == 200) {
                        this.plans = result.data || [];
                        this.filterPlans();
                    }
                });
            }
        });
    }

    // Filter plans by country (from step 1) and business type
    filterPlans(): void {
        const selectedCountry = this.form.get('country')?.value;
        const businessType = this.form.get('type')?.value; // 'register' for vendor, 'supplier' for supplier

       

        if (!selectedCountry || !this.plans) {
            this.filteredPlans = [];
            return;
        }

        // Determine the required role ID based on business type
        // From DB:
        //  - Vendor role _id: 6040d231000ac86f58793001 (type VA)
        //  - Supplier role _id: 655f32ce2f02fe110f8907d3 (type SP)
        const VENDOR_ROLE_ID = '6040d231000ac86f58793001';
        const SUPPLIER_ROLE_ID = '655f32ce2f02fe110f8907d3';
        const requiredRoleId = businessType === 'supplier' ? SUPPLIER_ROLE_ID : VENDOR_ROLE_ID;

        this.filteredPlans = this.plans.filter(plan => {
            

            // Exclude global default plans - they are auto-assigned to all countries and not for purchase
            if (plan.isGlobalDefault === true) {

                return false;
            }

            // Exclude custom assign plans - they are manually assigned and not for self-selection
            if (plan.isCustomAssign === true) {
                return false;
            }
            
            // Check if plan has country and price
            if (!plan.country) {
            
                return false;
            }
            const isActive = plan.status === 'A';
            // Check if price exists (0 is valid for free plans)
            const hasPrice = plan.monthly_price !== undefined && plan.monthly_price !== null && plan.monthly_price !== '' || 
                             plan.yearly_price !== undefined && plan.yearly_price !== null && plan.yearly_price !== '';
            if (!isActive || !hasPrice) {
               
                return false;
            }

            // Check userType - must be VA or VendorAdmin
            if (!plan.userType || (plan.userType !== 'VA' && plan.userType !== 'VendorAdmin')) {
               
                return false;
            }

            // roles in API are stored as string IDs
            const roles: string[] = Array.isArray(plan.roles) ? plan.roles : [];
            const hasRequiredRole = roles.includes(requiredRoleId);

            if (!hasRequiredRole) {
               
                return false;
            }

            // Get the country name from the plan (either English or Arabic)
            const planCountryEn = (plan.country.en || plan.country || '').toString().toLowerCase().trim();
            const planCountryAr = (plan.country.ar || '').toString().toLowerCase().trim();
            
            // Get selected country details
            const selectedCountryNameEn = (selectedCountry.en || selectedCountry || '').toString().toLowerCase().trim();
            const selectedCountryNameAr = (selectedCountry.ar || '').toString().toLowerCase().trim();
            
           
            
            // Match by exact country name match (English or Arabic)
            const exactMatch = 
                planCountryEn === selectedCountryNameEn || 
                planCountryAr === selectedCountryNameAr;
            
            // Handle common abbreviations
            const abbreviationMatch = this.matchCountryAbbreviation(planCountryEn, selectedCountryNameEn);
            
            // Also check if plan is for "All Countries"
            const isAllCountries = planCountryEn === 'all countries' || planCountryAr === 'جميع الدول';
            
            const matches = exactMatch || abbreviationMatch || isAllCountries;
           
            
            return matches;
        });
        
        // Sort plans: free/default plans first, then by name
        this.filteredPlans.sort((a, b) => {
            // Free plans (isFree or isDefault with 0 prices) come first
            const aIsFree = a.isFree || (a.isDefault && a.monthly_price === 0 && a.yearly_price === 0);
            const bIsFree = b.isFree || (b.isDefault && b.monthly_price === 0 && b.yearly_price === 0);
            
            if (aIsFree && !bIsFree) return -1;
            if (!aIsFree && bIsFree) return 1;
            
            // Then sort by name
            const aName = a.name?.en || a.name?.ar || '';
            const bName = b.name?.en || b.name?.ar || '';
            return aName.localeCompare(bName);
        });
        
      
        
        // Initialize billing types for filtered plans
        this.initializeBillingTypes();
        
        // Set pending plan if exists (from loaded vendor request)
        if (this.pendingPlanId && this.filteredPlans.length > 0) {
            const pendingPlan = this.filteredPlans.find(p => p._id === this.pendingPlanId);
            if (pendingPlan) {
                this.selectedPlan = pendingPlan;
                // Restore the billing type that was saved with the vendor request
                if (this.pendingBillingType) {
                    this.selectedBillingType[this.pendingPlanId] = this.pendingBillingType;
                }
            }
            this.pendingPlanId = null; // Clear pending
        }
    }

    // Helper method to match country abbreviations and variations
    private matchCountryAbbreviation(planCountry: string, selectedCountry: string): boolean {
        const countryMappings: { [key: string]: string[] } = {
            'saudi arabia': ['ksa', 'sa', 'saudi'],
            'united arab emirates': ['uae', 'emirates', 'u.a.e', 'u.a.e.'],
            'bahrain': ['bhr', 'bh'],
            'kuwait': ['kw', 'kwt'],
            'oman': ['om', 'omn'],
            'qatar': ['qa', 'qat'],
            'egypt': ['eg', 'egy'],
            'jordan': ['jo', 'jor'],
            'lebanon': ['lb', 'lbn'],
            'iraq': ['iq', 'irq']
        };

        // Check if planCountry is an abbreviation of the selectedCountry
        const selectedAbbreviations = countryMappings[selectedCountry] || [];
        if (selectedAbbreviations.includes(planCountry)) {
            return true;
        }

        // Check if selectedCountry is an abbreviation of the planCountry
        const planAbbreviations = countryMappings[planCountry] || [];
        if (planAbbreviations.includes(selectedCountry)) {
            return true;
        }

        // Check if planCountry contains selectedCountry or vice versa (partial match)
        if (planCountry.includes(selectedCountry) || selectedCountry.includes(planCountry)) {
            return true;
        }

        return false;
    }

    // Initialize billing types for all plans - set default to available option
    initializeBillingTypes(): void {
        this.filteredPlans.forEach(plan => {
            if (!this.selectedBillingType[plan._id]) {
                this.selectedBillingType[plan._id] = this.getDefaultBillingType(plan);
            }
        });
    }

    // Set billing type for a specific plan
    setBillingType(planId: string, type: 'monthly' | 'yearly'): void {
        this.selectedBillingType[planId] = type;
    }

    // Get the default billing type based on what's available
    getDefaultBillingType(plan: any): 'monthly' | 'yearly' {
        if (plan.monthly_price && plan.yearly_price) {
            return 'monthly';
        } else if (plan.monthly_price) {
            return 'monthly';
        } else if (plan.yearly_price) {
            return 'yearly';
        }
        return 'monthly';
    }

    // Get the display price based on selected billing type
    getDisplayPrice(plan: any): number {
        const billingType = this.selectedBillingType[plan._id] || this.getDefaultBillingType(plan);
        return billingType === 'monthly' ? plan.monthly_price : plan.yearly_price;
    }

    // Get the current billing type for a plan (with fallback)
    getCurrentBillingType(plan: any): 'monthly' | 'yearly' {
        return this.selectedBillingType[plan._id] || this.getDefaultBillingType(plan);
    }

    // Check if current billing type has active discount
    hasActiveDiscount(plan: any): boolean {
        const billingType = this.getCurrentBillingType(plan);
        if (billingType === 'monthly') {
            return this.isMonthlyDiscountActive(plan);
        } else {
            return this.isYearlyDiscountActive(plan);
        }
    }

    // Check if monthly discount is active
    private isMonthlyDiscountActive(plan: any): boolean {
        if (!plan.monthly_discount_rate || plan.monthly_discount_rate <= 0) return false;
        const now = new Date();
        const startDate = plan.monthly_discount_starts ? new Date(plan.monthly_discount_starts) : null;
        const endDate = plan.monthly_discount_ends ? new Date(plan.monthly_discount_ends) : null;
        return (!startDate || now >= startDate) && (!endDate || now <= endDate);
    }

    // Check if yearly discount is active
    private isYearlyDiscountActive(plan: any): boolean {
        if (!plan.yearly_discount_rate || plan.yearly_discount_rate <= 0) return false;
        const now = new Date();
        const startDate = plan.yearly_discount_starts ? new Date(plan.yearly_discount_starts) : null;
        const endDate = plan.yearly_discount_ends ? new Date(plan.yearly_discount_ends) : null;
        return (!startDate || now >= startDate) && (!endDate || now <= endDate);
    }

    // Get current discount rate
    getCurrentDiscountRate(plan: any): number {
        const billingType = this.getCurrentBillingType(plan);
        return billingType === 'monthly' ? plan.monthly_discount_rate : plan.yearly_discount_rate;
    }

    // Calculate final price after discount
    calculateFinalPrice(originalPrice: number, plan: any): number {
        if (!this.hasActiveDiscount(plan)) return originalPrice;
        const discountRate = this.getCurrentDiscountRate(plan);
        return originalPrice - (originalPrice * (discountRate / 100));
    }

    // Get currency code from plan
    getPlanCurrency(plan: any): string {
        if (!plan || !plan.currency) return 'BHD'; // Default fallback
        // Currency can be a Label object {en, ar} or a string
        if (typeof plan.currency === 'string') {
            return plan.currency;
        }
        return plan.currency.en || plan.currency.ar || 'BHD';
    }

    // Toggle features expansion for a plan
    toggleFeatures(planId: string): void {
        this.expandedFeatures[planId] = !this.expandedFeatures[planId];

        if (this.expandedFeatures[planId]) {
            const plan = (this.filteredPlans || []).find((p: any) => p?._id === planId) || (this.plans || []).find((p: any) => p?._id === planId);
            const featureIds: string[] = Array.isArray(plan?.subscription_features)
                ? plan.subscription_features.map((id: any) => this.toId(id))
                : Array.isArray(plan?.subscription_features_populated)
                    ? plan.subscription_features_populated.map((f: any) => this.toId(f?._id)).filter(Boolean)
                    : [];
            featureIds.forEach((fid) => this.ensureSubFeaturesLoaded(fid));
        }
    }

    private toId(value: any): string {
        if (value === null || value === undefined) return '';
        return String(value);
    }

    private ensureSubFeaturesLoaded(featureId: string): void {
        const fid = this.toId(featureId);
        if (!fid) return;
        if (this.subFeaturesLoaded[fid] === true) return;

        this.subFeaturesLoaded[fid] = true;
        this.vendorPlanFeatureService.getSubFeatures(fid).subscribe({
            next: (res: any) => {
                if (res?.code === 200) {
                    const list = Array.isArray(res.data) ? res.data : [];
                    this.subFeaturesByFeatureId.set(fid, list);
                    list.forEach((sf: any) => {
                        const sid = this.toId(sf?._id);
                        if (sid) this.subFeatureMap.set(sid, sf);
                    });
                } else {
                    this.subFeaturesByFeatureId.set(fid, []);
                }
            },
            error: () => {
                this.subFeaturesByFeatureId.set(fid, []);
            }
        });
    }

    getFeatureUsageLimit(plan: any, featureId: string): number | null {
        const fid = this.toId(featureId);
        if (!plan || !fid) return null;
        const limits = plan.feature_usage_limits || plan.featureUsageLimits;
        if (!limits || typeof limits !== 'object') return null;

        const raw = limits[fid] ?? limits[String(fid)];
        if (raw === null || raw === undefined || raw === '') return null;
        const num = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
        return Number.isFinite(num) ? num : null;
    }

    getSubFeatureUsageLimit(plan: any, subFeatureId: string): number | null {
        const sid = this.toId(subFeatureId);
        if (!plan || !sid) return null;
        const limits = plan.sub_feature_usage_limits || plan.subFeatureUsageLimits;
        if (!limits || typeof limits !== 'object') return null;

        const raw = limits[sid] ?? limits[String(sid)];
        if (raw === null || raw === undefined || raw === '') return null;
        const num = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
        return Number.isFinite(num) ? num : null;
    }

    getUsageLimitText(limit: number | null): string {
        if (limit === null || limit === undefined) return '';
        if (limit === -1) {
            const translated = this.translate.instant('unlimited');
            return translated && translated !== 'unlimited' ? translated : 'Unlimited';
        }
        return String(limit);
    }

    getPlanSubFeaturesForFeature(plan: any, featureId: string): any[] {
        const fid = this.toId(featureId);
        if (!plan || !fid) return [];
        const selectedIds = new Set<string>((Array.isArray(plan?.subscription_sub_features) ? plan.subscription_sub_features : []).map((id: any) => this.toId(id)));
        if (selectedIds.size === 0) return [];

        const list = this.subFeaturesByFeatureId.get(fid) || [];
        return list.filter((sf: any) => selectedIds.has(this.toId(sf?._id)));
    }

    // Get card color based on index
    getCardColor(index: number): any {
        const colors = [
            { 
                bg: 'from-blue-500 to-blue-700', 
                text: 'text-white', 
                badge: 'bg-yellow-400 text-blue-900',
                priceText: 'text-white',
                oldPriceText: 'text-white',
                iconText: 'text-white',
                buttonBg: 'bg-white',
                buttonText: 'text-blue-600',
                buttonHover: 'hover:bg-blue-50'
            },
            { 
                bg: 'from-purple-500 to-purple-700', 
                text: 'text-white',
                badge: 'bg-yellow-400 text-purple-900',
                priceText: 'text-white',
                oldPriceText: 'text-white',
                iconText: 'text-white',
                buttonBg: 'bg-white',
                buttonText: 'text-purple-600',
                buttonHover: 'hover:bg-purple-50'
            },
            { 
                bg: 'from-teal-500 to-teal-700', 
                text: 'text-white',
                badge: 'bg-yellow-400 text-teal-900',
                priceText: 'text-white',
                oldPriceText: 'text-white',
                iconText: 'text-white',
                buttonBg: 'bg-white',
                buttonText: 'text-teal-600',
                buttonHover: 'hover:bg-teal-50'
            },
            { 
                bg: 'from-orange-500 to-orange-700', 
                text: 'text-white',
                badge: 'bg-yellow-400 text-orange-900',
                priceText: 'text-white',
                oldPriceText: 'text-white',
                iconText: 'text-white',
                buttonBg: 'bg-white',
                buttonText: 'text-orange-600',
                buttonHover: 'hover:bg-orange-50'
            }
        ];
        return colors[index % colors.length];
    }

    // Select a plan (for card click)
    selectPlan(plan: any): void {
        this.selectedPlan = plan;

        const featureIds: string[] = Array.isArray(plan?.subscription_features)
            ? plan.subscription_features.map((id: any) => this.toId(id))
            : Array.isArray(plan?.subscription_features_populated)
                ? plan.subscription_features_populated.map((f: any) => this.toId(f?._id)).filter(Boolean)
                : [];
        featureIds.forEach((fid) => this.ensureSubFeaturesLoaded(fid));
    }

    // Check if a plan is selected
    isPlanSelected(plan: any): boolean {
        return this.selectedPlan && this.selectedPlan._id === plan._id;
    }

    // Subscribe to the selected plan (called by Next button in Step 2)
    subscribeToSelectedPlan(): void {
        if (!this.selectedPlan) {
            this.toastr.error(
                this.translate.instant('please_select_plan') || 'Please select a subscription plan',
                this.translate.instant('error')
            );
            return;
        }
        
        // Save the selected plan to vendor request and move to step 3
        this.savePlanToVendorRequest();
    }

    // Save plan selection to vendor request
    private savePlanToVendorRequest(): void {
       
        
        if (!this.vendorRequestId) {
            this.toastr.error(this.translate.instant('vendor_request_not_found') || 'Vendor request not found');
            return;
        }

        this.isLoading = true;
        const billingType = this.selectedBillingType[this.selectedPlan._id] || this.getDefaultBillingType(this.selectedPlan);
        
        const selectPlanData = {
            _id: this.vendorRequestId,
            planId: this.selectedPlan._id,
            billingType: billingType
        };
        
       

        this.vendorRequestService.selectPlan(selectPlanData).subscribe({
            next: (response: any) => {
                this.isLoading = false;
                if (response.code === 200) {
                    this.vendorRequestStatus = 'plan';
                    this.pendingPlanId = this.selectedPlan._id;
                    this.pendingBillingType = billingType;
                    this.toastr.success(this.translate.instant('plan_saved_successfully') || 'Plan saved successfully');
                    this.currentStep = 3;
                    // Start checking for authorization
                    this.startAuthorizationCheck();
                } else {
                    this.toastr.error(response.msg || 'Error saving plan selection');
                }
            },
            error: (error) => {
                this.isLoading = false;
                this.toastr.error(error?.error?.msg || error?.message || 'Error saving plan selection');
            }
        });
    }

    // Start polling for authorization status
    private startAuthorizationCheck(): void {
        // Clear any existing interval
        if (this.authorizationCheckInterval) {
            clearInterval(this.authorizationCheckInterval);
        }
        
        // Check immediately
        this.checkAuthorizationStatus();
        
        // Then check every 10 seconds
        this.authorizationCheckInterval = setInterval(() => {
            this.checkAuthorizationStatus();
        }, 10000);
    }

    // Check if vendor request has been authorized
    private checkAuthorizationStatus(): void {
        if (!this.vendorRequestId) return;
        
        this.vendorRequestService.getRequest(this.vendorRequestId).subscribe({
            next: (response: any) => {
                if (response.code === 200) {
                    const vendorRequest = response.data;
                    this.vendorRequestStatus = vendorRequest.status;
                    
                    // Check if status is 'completed' (activated by admin) or 'paid' (checkout finished)
                    if (vendorRequest.status === 'completed' || vendorRequest.status === 'paid') {
                        this.isAuthorized = true;
                        this.toastr.success(
                            this.translate.instant('you_have_been_authorized') || 'You have been authorized! You can now proceed.',
                            this.translate.instant('success'),
                            { timeOut: 5000 }
                        );
                        // Stop polling
                        if (this.authorizationCheckInterval) {
                            clearInterval(this.authorizationCheckInterval);
                            this.authorizationCheckInterval = null;
                        }
                        if (vendorRequest.status === 'paid') {
                            this.currentStep = 5;
                        }
                    }
                }
            },
            error: (error) => {
                console.error('Error checking authorization status:', error);
            }
        });
    }

    // Move to step 4 (Summary) after authorization
    proceedToSummary(): void {
        if (!this.isAuthorized) {
            this.toastr.error(
                this.translate.instant('waiting_for_authorization') || 'Please wait for authorization before proceeding',
                this.translate.instant('error')
            );
            return;
        }
        this.currentStep = 4;
    }

    // Go back to step 3 from step 4
    goBackToStep3(): void {
        this.currentStep = 3;
        // Start authorization check again in case status changes
        this.startAuthorizationCheck();
    }

    // Go back to step 2 to change the plan
    goBackToChangePlan(): void {
        // Stop authorization polling if running
        if (this.authorizationCheckInterval) {
            clearInterval(this.authorizationCheckInterval);
            this.authorizationCheckInterval = null;
        }
        
        // Go back to step 2
        this.currentStep = 2;
        this.loadPlans();
    }

    // Check if the selected plan is free
    isSelectedPlanFree(): boolean {
        if (!this.selectedPlan) return false;
        return this.selectedPlan.isFree || 
               (this.selectedPlan.isDefault && this.selectedPlan.monthly_price === 0 && this.selectedPlan.yearly_price === 0);
    }

    // Apply free plan directly without checkout
    applyFreePlan(): void {
        if (!this.selectedPlan) {
            this.toastr.error(
                this.translate.instant('please_select_plan') || 'Plan not found',
                this.translate.instant('error')
            );
            return;
        }

        if (!this.isSelectedPlanFree()) {
            this.toastr.error(
                this.translate.instant('plan_not_free') || 'This plan is not free',
                this.translate.instant('error')
            );
            return;
        }

        this.disableSubmitButton = true;

        // Apply free plan by updating the vendor request with the plan
        const currentUser = this.authService.currentUserValue;
        if (!currentUser || !currentUser._id) {
            this.toastr.error(
                this.translate.instant('authentication_required') || 'You need to login first',
                this.translate.instant('error')
            );
            this.disableSubmitButton = false;
            return;
        }

        // Use the dedicated applyFreePlan endpoint
        const applyData = {
            _id: this.vendorRequestId,
            planId: this.selectedPlan._id
        };

        this.vendorRequestService.applyFreePlan(applyData).subscribe(
            (response: any) => {
                if (response.code === 200) {
                    this.toastr.success(
                        this.translate.instant('free_plan_applied') || 'Free plan applied successfully!',
                        this.translate.instant('success')
                    );
                    // Move to confirmation step
                    this.currentStep = 5;
                } else {
                    this.toastr.error(
                        response.msg || this.translate.instant('error_applying_plan') || 'Error applying plan',
                        this.translate.instant('error')
                    );
                }
                this.disableSubmitButton = false;
            },
            (error: any) => {
                console.error('Error applying free plan:', error);
                this.toastr.error(
                    this.translate.instant('error_applying_plan') || 'Error applying plan. Please try again.',
                    this.translate.instant('error')
                );
                this.disableSubmitButton = false;
            }
        );
    }

    // Proceed to checkout from step 4
    proceedToCheckoutFromSummary(): void {
        if (!this.selectedPlan) {
            this.toastr.error(
                this.translate.instant('please_select_plan') || 'Plan not found',
                this.translate.instant('error')
            );
            return;
        }
        this.subscribeToplan(this.selectedPlan);
    }

    public translateWithFallback(key: string, fallback: string): string {
        const translated = this.translate.instant(key);
        if (!translated || translated === key) {
            return fallback;
        }
        return translated;
    }

    // Subscribe to a plan
    subscribeToplan(plan: any): void {
        const billingType = this.selectedBillingType[plan._id] || this.getDefaultBillingType(plan);
        
        // User is already authenticated (protected route)
        const currentUser = this.authService.currentUserValue;
        if (!currentUser || !currentUser._id) {
            this.toastr.error(
                this.translate.instant('authentication_required') || 'You need to login or register first',
                this.translate.instant('error')
            );
            return;
        }

        // Check if user already has an active subscription
        this.subscriptionService.checkUserSubscription(currentUser._id).pipe(
            take(1)
        ).subscribe(response => {
            if (response.code === 200) {
                const data = response.data;
                
                // If user has active subscription and it's NOT a default plan, block purchase
                if (data.hasActiveSubscription && !data.canPurchaseNewPlan) {
                    this.toastr.error(this.translate.instant('already_have_subscription') || 'You already have an active subscription plan. Please manage your existing subscription before purchasing a new one.');
                    return;
                }

                // User can proceed - either no subscription or has default plan that will be replaced
                this.proceedToCheckout(plan, billingType);
            } else {
                this.toastr.error(this.translate.instant('subscription_check_error') || 'Error checking subscription status. Please try again.');
            }
        }, error => {
            console.error('Error checking subscription:', error);
            this.toastr.error(this.translate.instant('subscription_check_error') || 'Error checking subscription status. Please try again.');
        });
    }

    // Proceed to checkout after validation
    private proceedToCheckout(plan: any, billingType: 'monthly' | 'yearly'): void {
        const priceField = billingType === 'monthly' ? 'monthly_price' : 'yearly_price';
        const selectedPrice = plan[priceField];
        
        // Calculate discount if applicable
        const discountRateField = billingType === 'monthly' ? 'monthly_discount_rate' : 'yearly_discount_rate';
        const discountStartsField = billingType === 'monthly' ? 'monthly_discount_starts' : 'yearly_discount_starts';
        const discountEndsField = billingType === 'monthly' ? 'monthly_discount_ends' : 'yearly_discount_ends';
        
        let discountRate = 0;
        let originalPrice = selectedPrice;
        let finalPrice = selectedPrice;
        
        // Check if discount is active
        if (plan[discountRateField] && plan[discountRateField] > 0) {
            const now = new Date();
            const discountStartDate = plan[discountStartsField] ? new Date(plan[discountStartsField]) : null;
            const discountEndDate = plan[discountEndsField] ? new Date(plan[discountEndsField]) : null;
            
            const isDiscountActive = (!discountStartDate || now >= discountStartDate) && 
                                    (!discountEndDate || now <= discountEndDate);
            
            if (isDiscountActive) {
                discountRate = plan[discountRateField];
                const discountAmount = originalPrice * (discountRate / 100);
                finalPrice = originalPrice - discountAmount;
            }
        }

        // Navigate to checkout-s route with subscription details
        const subscriptionType = billingType === 'monthly' ? 'monthly' : 'annualy';
        
        // Only pass subscription type and vendor request ID - backend will fetch all other details securely
        this.router.navigate(['/checkout-s', plan._id, 'vendor-subscription'], {
            queryParams: {
                st: subscriptionType,
                vendorRequestId: this.vendorRequestId // Pass vendor request ID to update status after payment
            }
        });
    }

    // Step 1 required fields for validation
    private step1RequiredFields = ['companyName', 'contactName', 'email', 'phone', 'crNumber', 'vatNo', 'type', 'country', 'crCopy', 'vatCopy', 'logo', 'billingAddress'];



    // Validate Step 1 fields
    private validateStep1(): boolean {
        let isValid = true;
        const missing: string[] = [];

        // Create dynamic required fields list based on business type
        const requiredFields = [...this.step1RequiredFields];
        
        // businessActivities is always required for both vendor and supplier
        requiredFields.push('businessActivities');

        requiredFields.forEach(field => {
            const ctrl = this.form.get(field);
            if (ctrl) {
                ctrl.markAsTouched({ onlySelf: true });
                if (ctrl.hasError('required')) {
                    isValid = false;
                    missing.push(this.requiredFieldMap[field] || field);
                } else if (field === 'email' && ctrl.hasError('email')) {
                    isValid = false;
                    missing.push('invalid_email');
                } else if ((field === 'phone' || field === 'crNumber' || field === 'vatNo') && ctrl.hasError('pattern')) {
                    isValid = false;
                    missing.push('numbers_only');
                }
            }
        });

        if (!isValid && missing.length) {
            const msg = missing.map(k => this.translate.instant(k)).join(', ');
            this.toastr.error(this.translate.instant('please_fill_required_fields') + ': ' + msg);
        }

        return isValid;
    }

    // Navigate to next step
    nextStep(): void {
        if (this.currentStep === 1) {
            if (!this.validateStep1()) {
                return;
            }
            
            // If we already have a vendorRequestId, just navigate to step 2 (no API call)
            if (this.vendorRequestId) {
                this.currentStep = 2;
                // Load subscription plans when moving to step 2
                this.loadPlans();
                return;
            }
            
            // Show confirmation dialog before proceeding (first time only)
            this.showStep1ConfirmationDialog();
        } else if (this.currentStep < this.totalSteps) {
            this.currentStep++;
        }
    }

    // Show confirmation dialog for step 1 data
    private showStep1ConfirmationDialog(): void {
        const countryValue = this.form.get('country').value;
        const countryDisplay = countryValue?.en || countryValue?.ar || countryValue || '';
        
        const billingAddressValue = this.form.get('billingAddress').value;
        const hasBillingAddress = billingAddressValue && billingAddressValue.companyName;
        
        const formData = {
            companyName: this.form.get('companyName').value,
            contactName: this.form.get('contactName').value,
            email: this.form.get('email').value,
            phone: this.form.get('phone').value,
            crNumber: this.form.get('crNumber').value,
            vatNo: this.form.get('vatNo').value,
            country: countryDisplay,
            billingAddress: hasBillingAddress ? this.getBillingAddressDisplay() : null
        };

        const dialogRef = this.dialog.open(ConfirmStep1DialogComponent, {
            width: '550px',
            panelClass: 'confirm-step1-dialog-panel',
            backdropClass: 'confirm-step1-backdrop-blur',
            data: {
                title: this.translate.instant('confirm_your_data') || 'Confirm Your Data',
                message: this.translate.instant('confirm_step1_message') || 'Please review your information below. Once you proceed, you will not be able to edit this data.',
                formData: formData
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result === true) {
                // User confirmed, save Step 1 data to database
                this.saveStep1Data();
            }
            // If result is false or undefined, user cancelled - do nothing
        });
    }

    // Navigate to previous step
    prevStep(): void {
        if (this.currentStep > 1) {
            this.currentStep--;
        }
    }

    // Go to specific step
    goToStep(step: number): void {
        if (step >= 1 && step <= this.totalSteps && step <= this.currentStep) {
            this.currentStep = step;
        }
    }

    // Save Step 1 data to database
    private saveStep1Data(): void {
        this.isLoading = true;
        
        let requestData: any = new FormData();
        const billingAddressValue = this.form.get('billingAddress').value;
        
        const step1Data: any = {
            companyName: this.form.get('companyName').value,
            contactName: this.form.get('contactName').value,
            email: this.form.get('email').value,
            phone: this.form.get('phone').value,
            crNumber: this.form.get('crNumber').value,
            vatNo: this.form.get('vatNo').value,
            type: this.form.get('type').value,
            country: this.form.get('country').value,
            affiliate: this.form.get('affiliate').value,
            step: 1, // Mark as step 1 submission
            status: 'new' // New vendor request
        };
        
        // Include billingAddress if it exists (serialize as JSON string for FormData)
        if (billingAddressValue) {
            step1Data.billingAddress = JSON.stringify(billingAddressValue);
        }
        
        // Include businessActivities (always sent now for both vendor and supplier)
        const businessActivitiesValue = this.form.get('businessActivities')?.value;
        if (businessActivitiesValue) {
            step1Data.businessActivities = JSON.stringify(businessActivitiesValue);
        }
        
        // Include itemCategoryIds (auto-populated from activities)
        const itemCategoryIdsValue = this.form.get('itemCategoryIds')?.value;
        if (itemCategoryIdsValue) {
            step1Data.itemCategoryIds = JSON.stringify(itemCategoryIdsValue);
        }
        
        requestData = this.fd.ToFormData(step1Data);
        
        // Append files if they exist
        if (this.form.get('crCopy').value) {
            requestData.append('crCopy', this.form.get('crCopy').value);
        }
        if (this.form.get('vatCopy').value) {
            requestData.append('vatCopy', this.form.get('vatCopy').value);
        }
        if (this.form.get('logo').value) {
            requestData.append('logo', this.form.get('logo').value);
        }

        // If we already have a request ID, update it; otherwise create new
        if (this.vendorRequestId) {
            requestData.append('_id', this.vendorRequestId);
        }

        this.vendorRequestService.createRequest(requestData).subscribe({
            next: (response: any) => {
                this.isLoading = false;
                if (response.code === 200) {
                    // Extract the ID as a string - handle both string and ObjectId formats
                    const newId = response.data?._id;
                    if (newId) {
                        this.vendorRequestId = typeof newId === 'string' ? newId : (newId.toString ? newId.toString() : String(newId));
                    }
                    
                   
                    
                    // Save to localStorage for page refresh recovery
                    if (this.vendorRequestId) {
                        localStorage.setItem(this.VENDOR_REQUEST_STORAGE_KEY, this.vendorRequestId);
                    }

                    // Lock Step 1 fields immediately after first successful save.
                    this.disableStep1Fields();
                    
                    this.toastr.success(this.translate.instant('step_1_saved_successfully') || 'Step 1 saved successfully');
                    this.currentStep = 2;
                    // Load subscription plans when moving to step 2
                    this.loadPlans();
                } else if (response.code === 201) {
                    this.toastr.warning(this.translate.instant('request_already_submitted_with_same_email') || 'A request has already been submitted with this email.');
                } else if (response.code === 400) {
                    this.toastr.error(response.msg);
                } else {
                    this.toastr.error(response.msg || 'An error occurred while saving your data.');
                }
            },
            error: (error) => {
                this.isLoading = false;
                this.toastr.error(error?.message || 'An error occurred while saving your data.');
            }
        });
    }

    private requiredFieldMap: { [key: string]: string } = {
        companyName: 'company_name',
        contactName: 'contact_name',
        email: 'email',
        phone: 'phone',
        country: 'country',
        crNumber: 'cr_number',
        vatNo: 'vat_no',
        crCopy: 'cr_document',
        vatCopy: 'vat_certificate',
        logo: 'company_logo',
        itemCategoryIds: 'categories',
        billingAddress: 'company_billing_address'
    };

    private getMissingRequiredFields(): string[] {
        const missing: string[] = [];
        Object.keys(this.requiredFieldMap).forEach(key => {
            const ctrl = this.form.get(key);
            if (!ctrl) return;
            // Mark as touched so UI can reflect errors if template adds indicators later
            ctrl.markAsTouched({ onlySelf: true });
            if (ctrl.hasError('required')) {
                missing.push(this.requiredFieldMap[key]);
            } else if (key === 'email' && ctrl.hasError('email')) {
                // Distinguish invalid email separately
                missing.push('invalid_email');
            } else if ((key === 'phone' || key === 'crNumber' || key === 'vatNo') && ctrl.hasError('pattern')) {
                missing.push('numbers_only');
            }
        });
        return missing;
    }

    submit() {
        if (this.form.invalid) {
            const missing = this.getMissingRequiredFields();
            if (missing.length) {
                // Localize each key through translate pipe at template normally; here use toastr directly.
                // Compose a readable message: translate keys joined by comma.
                const msg = missing.map(k => this.translate.instant(k)).join(', ');
                this.toastr.error(this.translate.instant('please_fill_required_fields') + ': ' + msg);
            } else {
                this.toastr.error(this.translate.instant('form_invalid'));
            }
            return;
        }

        this.disableSubmitButton = true;
        // this.form.get('extraServices').setValue(this.extraServices);

        let rawValues = this.form.getRawValue();
        // Stringify billingAddress to prevent FormDataHelper from flattening it into billingAddress[key]
        // ensuring the backend receives it as a single JSON string field
        if (rawValues.billingAddress && typeof rawValues.billingAddress === 'object') {
            rawValues.billingAddress = JSON.stringify(rawValues.billingAddress);
        }

        let requestData: any = new FormData();
        requestData = this.fd.ToFormData(rawValues); // Use getRawValue to include disabled fields
        this.vendorRequestService.createRequest(requestData).subscribe((response: any) => {
            this.disableSubmitButton = false;
            if (response.code == 200) {
                this.toastr.success(`Your request has been successfully submitted.`);
                this.router.navigate(['/vendor-request-success']);
            } else if (response.code == 201) {
                this.toastr.warning(`Your request has already been submitted with same email.`);
            } else if (response.code == 400) {
                // Show error for already approved vendors or other validation errors
                this.toastr.error(response.msg);
            }
            else {
                this.toastr.error(response.msg || 'An error occurred while submitting your request.');
            }
        }, (error) => {
            this.disableSubmitButton = false;
            this.toastr.error(error?.message || 'An error occurred while submitting your request.');
        });
    }

    /* isSelected(serviceId) {
        return this.extraServices.includes(serviceId);
    }

    updateServices(event) {
        const service = event.source.value;
        if (event.checked) {
            if (service.group && service.group?.length > 0) {
                let sameGroupIds = _.map(_.filter(this.chosenPlan?.services || [], (serv) => { return serv.group == service.group }), '_id');
                if (sameGroupIds.length > 0) _.remove(this.extraServices, (serviceId) => { return sameGroupIds.includes(serviceId) });
            }
            this.extraServices.push(service._id);
            this.extraServices = _.uniq(this.extraServices);
        } else {
            _.remove(this.extraServices, (serviceId) => { serviceId == service._id; });
        }
    } */

    handleFileInput(name, file: any) {
        let acceptedTypes = [
            "application/pdf",
            "image/jpeg",
            "image/png"
        ];

        if (name == 'logo') {
            acceptedTypes.push("image/svg+xml");
        }

        for (let item of file) {
            let reader = new FileReader();
            reader.readAsDataURL(item);

            this.fileErrors[name].type = !acceptedTypes.includes(item.type);
            this.fileErrors[name].size = (item.size / 1024 / 1024) > 2;

            if (this.fileErrors[name].type || this.fileErrors[name].size) {
                return;
            }

            this.form.get(name).patchValue(item);
        }
    }

    remove(name, element) {
        element.value = '';
        this.form.get(name).patchValue(null);
    }

    // Allow only numbers in input fields
    onlyNumbers(event: KeyboardEvent): boolean {
        const charCode = event.which ? event.which : event.keyCode;
        // Allow: backspace, delete, tab, escape, enter
        if ([8, 9, 27, 13, 46].includes(charCode)) {
            return true;
        }
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        if ((event.ctrlKey || event.metaKey) && [65, 67, 86, 88].includes(charCode)) {
            return true;
        }
        // Allow: 0-9
        if (charCode >= 48 && charCode <= 57) {
            return true;
        }
        // Block everything else
        event.preventDefault();
        return false;
    }

    // Open billing address dialog
    openBillingAddressDialog(): void {
        let currentBillingAddress = this.form.get('billingAddress').value || {};

        // Handle case where billingAddress is a string (legacy/bug fix)
        if (typeof currentBillingAddress === 'string') {
            try {
                currentBillingAddress = JSON.parse(currentBillingAddress);
            } catch (e) {
                if (currentBillingAddress === '[object Object]') {
                    currentBillingAddress = {};
                }
            }
        }

        const currentCompanyName = this.form.get('companyName').value;
        const currentCountry = this.form.get('country').value;
        
        // Find the country ID if we have a country name
        let countryId = currentBillingAddress.countryId;
        if (!countryId && currentCountry) {
            const matchedCountry = this.countriesWithCities.find(c => 
                (c.name?.en === currentCountry?.en || c.name?.en === currentCountry) ||
                (c.name?.ar === currentCountry?.ar || c.name?.ar === currentCountry)
            );
            if (matchedCountry) {
                countryId = matchedCountry._id;
            }
        }
        
        const dialogRef = this.dialog.open(BillingAddressDialogComponent, {
            width: '600px',
            maxWidth: '90vw',
            panelClass: 'billing-address-dialog-container',
            backdropClass: 'confirm-step1-backdrop-blur',
            data: {
                companyName: currentBillingAddress.companyName || currentCompanyName,
                country: currentBillingAddress.country || currentCountry,
                countryId: countryId,
                cityId: currentBillingAddress.cityId,
                areaId: currentBillingAddress.areaId,
                streetAddress: currentBillingAddress.streetAddress,
                building: currentBillingAddress.building,
                block: currentBillingAddress.block,
                flat: currentBillingAddress.flat,
                road: currentBillingAddress.road,
                postalCode: currentBillingAddress.postalCode,
                countries: this.countries,
                countriesWithCities: this.countriesWithCities,
                translateHelper: this.translateHelper
            },
            disableClose: false
        });
        
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.form.get('billingAddress').patchValue(result);
                this.form.get('billingAddress').markAsTouched();
                
                // Update main form fields from billing address (reverse sync)
                if (result.companyName && result.companyName !== this.form.get('companyName').value) {
                    this.form.get('companyName').patchValue(result.companyName, { emitEvent: false });
                }
                if (result.country && !this.compareCountry(result.country, this.form.get('country').value)) {
                    this.form.get('country').patchValue(result.country, { emitEvent: false });
                }
            }
        });
    }
    
    // Open business activities dialog
    openBusinessActivitiesDialog(): void {
        const dialogRef = this.dialog.open(BusinessActivitiesDialogComponent, {
            width: '900px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            panelClass: 'business-activities-dialog-container',
            backdropClass: 'business-activities-backdrop',
            hasBackdrop: true,
            data: {
                selectedActivities: this.selectedActivities
            },
            disableClose: false
        });
        
        dialogRef.afterClosed().subscribe(result => {
            if (result && Array.isArray(result)) {
                this.selectedActivities = result;
                
                // Extract activity IDs
                const activityIds = result.map(a => a._id);
                this.form.get('businessActivities').setValue(activityIds);
                this.form.get('businessActivities').markAsTouched();
                
                // Collect all categories from selected activities
                const allCategories: string[] = [];
                result.forEach(activity => {
                    if (activity.categories && Array.isArray(activity.categories)) {
                        activity.categories.forEach((cat: any) => {
                            const catId = cat?.$oid || cat._id || cat;
                            if (catId && !allCategories.includes(catId)) {
                                allCategories.push(catId);
                            }
                        });
                    }
                });
                
                // Update itemCategoryIds with collected categories
                this.form.get('itemCategoryIds').setValue(allCategories);
                this.form.get('itemCategoryIds').markAsTouched();
            }
        });
    }

    private normalizeIdArray(value: any): any[] {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed : [value];
            } catch (e) {
                return [value];
            }
        }
        if (typeof value === 'object') {
            return Object.values(value);
        }
        return [value];
    }

    private loadActivitiesByIds(activityIds: any[]): void {
        if (!activityIds || activityIds.length === 0) {
            return;
        }
        
        // Extract actual IDs (handle MongoDB ObjectId format)
        const ids = activityIds.map((id: any) => {
            if (typeof id === 'string') return id;
            if (id?.$oid) return id.$oid;
            if (id?._id) return id._id;
            return id;
        }).filter(id => !!id);
        
        if (ids.length === 0) {
            return;
        }
        
        // Build query string with IDs
        const idsParam = ids.join(',');
        const url = `${environment.apiUrl}/industry-activities?_id=${idsParam}&status=A`;
        
        // Use HttpClient to get activities by IDs
        this.http.get<any>(url).subscribe({
            next: (response) => {
                if (response && response.code === 200 && response.data) {
                    this.selectedActivities = response.data;
                    this.cdr.detectChanges();
                }
            },
            error: (error) => {
                console.error('Error loading activities:', error);
            }
        });
    }
    
    // Get business activities display text
    getBusinessActivitiesDisplay(): string {
        if (!this.selectedActivities || this.selectedActivities.length === 0) {
            return this.translate.instant('click_to_select_business_activities');
        }
        
        const count = this.selectedActivities.length;
        if (count === 1) {
            return this.translateHelper.localize(this.selectedActivities[0].name);
        }
        
        return this.translate.instant('n_activities_selected', { count });
    }
    
    // Get billing address display text
    getBillingAddressDisplay(): string {
        const address = this.form.get('billingAddress').value;
        if (!address || !address.companyName) {
            return this.translate.instant('click_to_add_billing_address');
        }
        
        // Get city name from cityId if available
        let cityName = address.city;
        if (address.cityId && this.countriesWithCities && this.countriesWithCities.length > 0) {
            const country = this.countriesWithCities.find(c => c._id === address.countryId);
            if (country && country.areas) {
                for (const area of country.areas) {
                    const city = area.cities?.find((c: any) => c._id === address.cityId);
                    if (city) {
                        cityName = this.translateHelper.localize(city.name);
                        break;
                    }
                }
            }
        }
        
        const addressParts = [
            address.streetAddress,
            address.building,
            address.block,
            address.flat,
            address.road
        ].filter(p => p).join(', ');
        
        const parts = [
            address.companyName,
            addressParts,
            cityName,
            address.postalCode,
            this.translateHelper.localize(address.country)
        ].filter(p => p);
        return parts.join(', ');
    }

    // Navigate to subscription management in admin panel
    goToSubscriptionManagement(): void {
        // Call the backend to get the exact admin URL (same pattern as quoteRedirect)
        this.vendorRequestService.getAdminUrl().subscribe({
            next: (response: any) => {
                if (response.code === 200 && response.url) {
                    // Backend returns the exact admin URL with proper language prefix and domain
                    window.location.href = response.url;
                } else {
                    // Fallback: show error message
                    this.toastr.error(
                        response.msg || 'Unable to navigate to subscription management',
                        'Error'
                    );
                }
            },
            error: (error) => {
                console.error('Error getting admin URL:', error);
                this.toastr.error(
                    'Unable to navigate to subscription management. Please ensure you are logged in.',
                    'Error'
                );
            }
        });
    }
}
